import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// V2 Components
import { MyFoldersMenuView } from '../views/Menu';
import ThemeSwitch from '../components/ThemeSwitch';
import StorageUsage from '../components/StorageUsage';
import DashboardNav from '../components/Dashboard/Nav';
import { useAuthStore } from '../../stores/authStore';

// Icons
import {
  UserIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  MenuIcon,
  CrossIcon,
  LogoutIcon,
  GridIcon
} from '../icons';

// ── Sidebar shell — always renders logo + close button on mobile ──────────────
const SidebarShell = ({ onClose, children }) => (
  <div className="w-[250px] h-full border-r border-[#ECECEC] dark:border-[#202020] bg-white dark:bg-[#121212] flex flex-col">
    {/* Logo */}
    <div className="p-6 flex items-center justify-between shrink-0">
      <div className="flex items-center">
        <span className="text-2xl font-bold text-[#121212] dark:text-[#D0D0D0]">Clipboarded</span>
        <span className="text-2xl font-normal italic text-[#121212] dark:text-[#D0D0D0] ml-1">.me</span>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="lg:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A] transition-colors"
          aria-label="Close menu"
        >
          <CrossIcon size={18} className="text-[#7B7B7B] dark:text-[#848484]" />
        </button>
      )}
    </div>

    {/* Nav content (default or custom) */}
    {children}
  </div>
);

// ── Default sidebar nav (dashboard pages) ────────────────────────────────────
const DefaultSidebarContent = () => (
  <>
    <div className="px-6 space-y-6 flex-1">
      <DashboardNav />
      <MyFoldersMenuView />
    </div>
    <div className="px-6 pb-6">
      <StorageUsage onUpgradeClick={() => console.log('Upgrade storage clicked')} />
    </div>
  </>
);

// ── User dropdown ───────────────────────────────────────────────────────────
const UserDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { logout } = useAuthStore();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate('/sign-in');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-[38px] h-[38px] bg-[#F1F1F1] dark:bg-[#2A2A2A] rounded-xl flex items-center justify-center hover:bg-[#E5E5E5] dark:hover:bg-[#3A3A3A] transition-colors"
        aria-label="User menu"
      >
        <UserIcon size={20} className="text-[#7B7B7B] dark:text-[#848484]" />
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] w-44 bg-white dark:bg-[#1A1A1A] border border-[#ECECEC] dark:border-[#202020] rounded-xl shadow-lg overflow-hidden z-50">
          <button
            onClick={() => { setOpen(false); navigate('/my/settings'); }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium text-[#121212] dark:text-[#D0D0D0] hover:bg-[#F7F7F7] dark:hover:bg-[#252525] transition-colors"
          >
            <GridIcon size={14} className="text-[#7B7B7B] dark:text-[#848484]" />
            Settings
          </button>
          <div className="h-px bg-[#ECECEC] dark:bg-[#202020]" />
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-xs font-medium text-[#121212] dark:text-[#D0D0D0] hover:bg-[#F7F7F7] dark:hover:bg-[#252525] transition-colors"
          >
            <LogoutIcon size={14} className="text-[#7B7B7B] dark:text-[#848484]" />
            Log out
          </button>
        </div>
      )}
    </div>
  );
};

// ── Layout ────────────────────────────────────────────────────────────────────
const DashboardLayout = ({
  children,
  sidebarContent,           // optional: replaces the default nav inside the sidebar
  onMenuItemClick = () => {},
  onBackClick = () => {},
  onForwardClick = () => {},
  className = ''
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const closeSidebar = () => setIsSidebarOpen(false);
  const desktopNav = typeof sidebarContent === 'function' ? sidebarContent(() => {}) : (sidebarContent ?? <DefaultSidebarContent />);
  const mobileNav  = typeof sidebarContent === 'function' ? sidebarContent(closeSidebar) : (sidebarContent ?? <DefaultSidebarContent />);

  return (
    <div className={`flex h-screen bg-white dark:bg-[#121212] ${className}`}>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-[250px] h-full shrink-0">
        <SidebarShell>
          {desktopNav}
        </SidebarShell>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden flex">
          <div className="h-full w-fit relative z-10">
            <SidebarShell onClose={closeSidebar}>
              {mobileNav}
            </SidebarShell>
          </div>
          <div
            className="flex-1 bg-black/50"
            onClick={closeSidebar}
            aria-hidden="true"
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="h-[72px] border-b border-[#ECECEC] dark:border-[#202020] bg-white dark:bg-[#121212] flex items-center justify-between px-4 lg:px-6">
          {/* Left Side */}
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Hamburger (mobile only) */}
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden w-10 h-10 bg-[#F1F1F1] dark:bg-[#2A2A2A] rounded-xl flex items-center justify-center hover:bg-[#E5E5E5] dark:hover:bg-[#3A3A3A] transition-colors"
              aria-label="Open menu"
            >
              <MenuIcon size={20} className="text-[#121212] dark:text-[#D0D0D0]" />
            </button>

            {/* Navigation Arrows */}
            <div className="flex items-center gap-2">
              <button
                onClick={onBackClick}
                className="hidden w-10 h-10 bg-[#F1F1F1] dark:bg-[#2A2A2A] rounded-xl flex items-center justify-center hover:bg-[#E5E5E5] dark:hover:bg-[#3A3A3A] transition-colors"
                aria-label="Go back"
              >
                <ArrowLeftIcon size={20} className="text-[#121212] dark:text-[#D0D0D0]" />
              </button>
              <button
                onClick={onForwardClick}
                className="hidden w-10 h-10 bg-[#F1F1F1] dark:bg-[#2A2A2A] rounded-xl flex items-center justify-center hover:bg-[#E5E5E5] dark:hover:bg-[#3A3A3A] transition-colors"
                aria-label="Go forward"
              >
                <ArrowRightIcon size={20} className="text-[#121212] dark:text-[#D0D0D0]" />
              </button>
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            <UserDropdown />
            <ThemeSwitch />
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>

    </div>
  );
};

export default DashboardLayout;
